// Architeia Ecosystem Shared Types
// This file defines the common types used across all Architeia services
// ============================================================================
// MINGLE MAGE SEQUENCE API TYPES
// ============================================================================
export const MINGLE_MAGE_LEGACY_SEQUENCE_DOCUMENT_VERSION = 'mingle-mage-action-sequence-0.1';
export const MINGLE_MAGE_SEQUENCE_DOCUMENT_VERSION = 'mingle-mage-action-sequence-0.2';
// ============================================================================
// MINGLE MAGE PROP DEFINITION API TYPES
// ============================================================================
export const MINGLE_MAGE_PROP_DEFINITION_DOCUMENT_VERSION = 'mingle-mage-prop-definition-0.1';
/** The only `kind` MM-450 writes; the column exists so stacks/gaffs join the same table. */
export const MINGLE_MAGE_PROP_DEFINITION_KIND_DECK_CONFIGURATION = 'deck-configuration';
// ============================================================================
// MINGLE MAGE ACCOUNT PREFERENCES API TYPES (MM-729)
// ============================================================================
export const MINGLE_MAGE_PREFERENCES_DOCUMENT_VERSION = 'mingle-mage-preferences-0.1';
/**
 * The settings that follow the account. Device-level settings (theme, language override, layout,
 * panel states, recents, restore-last-session) are deliberately absent: they stay in the browser.
 * Adding a key is a change here, in the service's allow-list schema and in the app's reader.
 */
export const MINGLE_MAGE_ACCOUNT_PREFERENCE_KEYS = [
    'defaultDeckConfig',
    'defaultRankOrder',
    'defaultSuitOrder',
    'defaultSequenceTemplate',
    'useDefaultTemplate',
    'cardDisplayMode',
    'cardDisplayPreset',
    'cardOrientationDisplayMode',
    'cardRelevanceDisplayMode',
    'stateStripDisplay',
    'stateStripLayout',
    'phaseRowDisplay',
    'trackNonDeterministicOutcomes',
    'favouritePaletteEntryIds'
];
/** Bounds enforced by the service (and mirrored by the app before it sends a change). */
export const MAX_MINGLE_MAGE_PREFERENCE_LIST_ENTRIES = 100;
export const MAX_MINGLE_MAGE_PREFERENCE_FAVOURITES = 200;
export const MAX_MINGLE_MAGE_PREFERENCES_BODY_BYTES = 65536;
// ============================================================================
// MINGLE MAGE OWNED USER DOCUMENTS: SETUPS AND SAVED ACTION PRESETS (MM-730)
// ============================================================================
export const MINGLE_MAGE_SETUP_DOCUMENT_VERSION = 'mingle-mage-setup-0.1';
export const MINGLE_MAGE_SAVED_ACTION_PRESET_DOCUMENT_VERSION = 'mingle-mage-saved-action-preset-0.1';
/** Per-owner count bounds (an abuse limit, not an invariant; mirrors MAX_PROP_DEFINITIONS_PER_OWNER). */
export const MAX_MINGLE_MAGE_SETUPS_PER_OWNER = 500;
export const MAX_MINGLE_MAGE_SAVED_ACTION_PRESETS_PER_OWNER = 500;
export const MAX_MINGLE_MAGE_OWNED_DOCUMENT_BYTES = 131072;
// ============================================================================
// MINGLE MAGE FACTORY CATALOG API TYPES (MM-727)
// ============================================================================
/** Permission that gates every `/admin/factory-catalog` route. */
export const MINGLE_MAGE_FACTORY_ADMIN_PERMISSION = 'mingle-mage:factory:admin';
// ============================================================================
// PRODUCT STAFF PERMISSIONS (KORA-184)
// ============================================================================
/**
 * Every product staff permission Kora can grant. Kora holds the grants (a platform admin issues
 * them in the Kora admin UI); Mingle Mage and Atlas read the caller's active set from Kora and
 * never from the access token's `permissions` claim. Adding a permission here is the only code
 * change Kora needs to start accepting grants for it. The names are the ones the Auth0 permissions
 * used, so existing call sites and docs keep their wording.
 */
export const SECRET_ORG_ROOMS_ADMIN_PERMISSION = 'secret-org:rooms:admin';
export const STAFF_PERMISSIONS = [
    'mingle-mage:releases:review',
    'mingle-mage:moderation:triage',
    'mingle-mage:moderation:review',
    'mingle-mage:moderation:admin',
    'mingle-mage:catalog:author',
    'mingle-mage:catalog:review',
    'mingle-mage:registry:review',
    'mingle-mage:registry:entitle',
    'mingle-mage:registry:publish',
    MINGLE_MAGE_FACTORY_ADMIN_PERMISSION,
    'atlas:catalog:review',
    // ORG-48: create, archive, re-level rooms and manage who may enter them (Secret Org).
    SECRET_ORG_ROOMS_ADMIN_PERMISSION
];
export function isStaffPermission(value) {
    return typeof value === 'string' && STAFF_PERMISSIONS.includes(value);
}
export const STAFF_PERMISSION_TEMPLATES = [
    {
        // "Standard admin": every staff duty. Derived from the catalog, so it grows with it.
        id: 'platform-staff',
        permissions: STAFF_PERMISSIONS
    }
];
export function findStaffPermissionTemplate(id) {
    return STAFF_PERMISSION_TEMPLATES.find(template => template.id === id);
}
/** ADR-007 rule 5 — relations stay within a tier. Mirrors services/atlas `ENTRY_TIER`; pinned both
 * ways in `services/atlas/src/services/curationContract.ts`. UX only — the server is authoritative. */
export const ATLAS_CATALOG_ENTRY_TIER = {
    trick: 'effect',
    effect: 'effect',
    routine: 'effect',
    technique: 'technique',
    sleight: 'technique',
    principle: 'technique'
};
export const ATLAS_CURATION_NOTE_MAX_LENGTH = 1000;
/**
 * The Atlas curation HTTP surface (ATLAS-21): one row per route under `/api/v1/curation`. `path` is
 * the Fastify route template (`:uuid` is the entry id); `operation` is the `AtlasCurationClient`
 * method that calls it. Neither side may import the other, so both are tested against this list:
 * `services/atlas` must register exactly these routes, and `libs/api-client` must request exactly
 * these. A path that drifts on either side fails a test instead of 404-ing in the admin panel.
 */
export const ATLAS_CURATION_ROUTES = [
    { operation: 'createProposal', method: 'POST', path: '/api/v1/curation/proposals' },
    { operation: 'listMyProposals', method: 'GET', path: '/api/v1/curation/proposals/mine' },
    { operation: 'getEntry', method: 'GET', path: '/api/v1/curation/entries/:uuid' },
    { operation: 'editEntry', method: 'PUT', path: '/api/v1/curation/entries/:uuid' },
    { operation: 'listQueue', method: 'GET', path: '/api/v1/curation/queue' },
    { operation: 'listEvents', method: 'GET', path: '/api/v1/curation/entries/:uuid/events' },
    { operation: 'publishEntry', method: 'POST', path: '/api/v1/curation/entries/:uuid/publish' },
    { operation: 'rejectEntry', method: 'POST', path: '/api/v1/curation/entries/:uuid/reject' },
    { operation: 'unpublishEntry', method: 'POST', path: '/api/v1/curation/entries/:uuid/unpublish' },
    { operation: 'reopenEntry', method: 'POST', path: '/api/v1/curation/entries/:uuid/reopen' }
];
/** Length limits at the curation write boundary (proposals/edits). Mirrors the service's
 * `CURATION_PROPOSAL_LIMITS`; `services/atlas/src/services/curationContract.ts` pins both ways at
 * the type level. The frontend uses it for inline validation only (UX — the server is authoritative). */
export const ATLAS_CURATION_PROPOSAL_LIMITS = {
    titleMax: 200,
    descriptionMax: 4000,
    listItemsMax: 50,
    listItemLengthMax: 64,
    aliasesMax: 20,
    aliasLengthMax: 200
};
/** Mirrors services/atlas `CURATION_RELATIONS_MAX`; pinned both ways in `curationContract.ts`. */
export const ATLAS_CURATION_RELATIONS_MAX = 50;
export const ATLAS_CURATION_RELATION_VIOLATION_CODES = [
    'self-edge',
    'target-not-found',
    'target-archived',
    'untyped-endpoint',
    'tier-mismatch',
    'variation-of-cycle',
    'variation-depth-exceeded',
    'multiple-variation-of',
    'similar-to-both-orientations'
];
// ============================================================================
// PRODUCT CAPABILITY GRANTS  (MM-537 D2 / KORA-73)
// ============================================================================
/** The only envelope schema version Kora accepts. A new field means version 2. */
export const CAPABILITY_GRANT_SCHEMA_VERSION = 1;
/** Target-product literal for Mingle Mage. Kora treats target products as opaque strings. */
export const TARGET_PRODUCT_MINGLE_MAGE = 'mingle-mage';
// ============================================================================
// LICENSED PROCEDURE REFERENCES (MM-616)
// ============================================================================
/** The only body schema version the server accepts. A new field means version 2. */
export const LICENSED_PROCEDURE_BODY_SCHEMA_VERSION = 1;
/** The single placeholder prop id a licensed procedure body may bind (D8). */
export const LICENSED_PROCEDURE_TARGET_PLACEHOLDER = '$target';
export const CONSENT_DURATIONS = ['7d', '30d', '90d', 'until-withdrawn'];
// ============================================================================
// ACCOUNT DATA CONTROLS (KORA-125)
// ============================================================================
/** The reasons an administrator may give when erasing an account on a member's behalf. */
export const ADMIN_ERASURE_REASONS = [
    'member_request',
    'member_request_verified',
    'policy'
];
/**
 * How a deleted account's published work is attributed when it stays (KORA-174): under the
 * member's name (the default; attribution is never stripped without the author's consent) or
 * under a generic handle.
 */
export const PUBLISHED_WORK_ATTRIBUTIONS = ['name', 'handle'];
// Account preferences (KORA-122).
/** Languages an account may choose. Bare tags only (KORA-122 D1); a product may ship a subset. */
export const SUPPORTED_LOCALES = ['en', 'de'];
export const DEFAULT_LOCALE = 'en';
/** Each language in its own language (ADR-010 D10). Never translated; shown by language pickers. */
export const LANGUAGE_ENDONYMS = {
    en: 'English',
    de: 'Deutsch'
};
export { MINGLE_MAGE_PLAN_FIXTURES } from './mingleMagePlanFixtures.js';
/**
 * The published versions of each legal document, oldest first; the last entry is current. Kora Web
 * serves exactly these (one text per version and locale, never edited after publication) and Kora
 * reads the current Terms version from here. Publishing a version means appending an entry and
 * adding its text files in `apps/web-dashboard/src/legal/content`.
 */
export const LEGAL_DOCUMENT_VERSIONS = {
    terms: [
        {
            version: '1.0',
            effectiveFrom: '2026-11-01',
            summary: {
                en: 'The first version of the terms of use.',
                de: 'Die erste Fassung der Nutzungsbedingungen.'
            }
        }
    ],
    privacy: [
        {
            version: '1.0',
            effectiveFrom: '2026-11-01',
            summary: {
                en: 'The first version of the privacy notice.',
                de: 'Die erste Fassung der Datenschutzerklärung.'
            }
        }
    ],
    impressum: [
        {
            version: '1.0',
            effectiveFrom: '2026-11-01',
            summary: { en: 'The first version of the imprint.', de: 'Die erste Fassung des Impressums.' }
        }
    ]
};
export function currentLegalVersion(key) {
    const versions = LEGAL_DOCUMENT_VERSIONS[key];
    return versions[versions.length - 1];
}
function majorOf(version) {
    return version.split('.')[0] ?? version;
}
/**
 * True when a member must accept the Terms again: they never accepted, or the current MAJOR is
 * newer than the one they accepted. A MINOR bump alone does not ask again.
 */
export function termsAcceptanceRequired(acceptedVersion) {
    if (acceptedVersion === null)
        return true;
    return majorOf(acceptedVersion) !== majorOf(currentLegalVersion('terms').version);
}
